import { useState, useEffect, useCallback } from "react";
import { aiApi } from "@/lib/apiClient";
import { DEFAULT_LAYERS } from "@/components/reader/readingEngine";

const PREFS_KEY = "reading_os_prefs";
const loadPrefs = () => {
    try { return JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") || {}; } catch (e) { return {}; }
};
const savePref = (patch) => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...patch })); } catch (e) {}
};

/**
 * Loads AI Smart Highlights for a document and manages which layers are on.
 * Generation is lazy + cached server-side: it only runs when highlights are
 * enabled and a reading mode that shows the full text (Guided/Focus) is active.
 */
export function useHighlights(docId, { active }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [enabled, setEnabledState] = useState(() => loadPrefs().highlightsOn ?? true);
    const [layers, setLayers] = useState(() => ({ ...DEFAULT_LAYERS, ...(loadPrefs().highlightLayers || {}) }));

    const load = useCallback(async (refresh = false) => {
        setLoading(true);
        setError(null);
        try {
            const res = await aiApi.highlights(docId, "auto", refresh);
            setData(res.data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Could not analyze document");
        } finally {
            setLoading(false);
        }
    }, [docId]);

    useEffect(() => {
        if (active && enabled && !data && !loading && !error) load();
    }, [active, enabled, data, loading, error, load]);

    const setEnabled = useCallback((v) => {
        setEnabledState(v);
        savePref({ highlightsOn: v });
    }, []);

    const toggleLayer = useCallback((id) => {
        setLayers((prev) => {
            const next = { ...prev, [id]: !prev[id] };
            savePref({ highlightLayers: next });
            return next;
        });
    }, []);

    // Apply a whole layer set at once (Smart Highlight presets). Turns the master
    // switch on so the effect is visible immediately. No new AI call.
    const applyLayers = useCallback((next) => {
        setLayers(next);
        savePref({ highlightLayers: next });
        setEnabledState(true);
        savePref({ highlightsOn: true });
    }, []);

    return {
        data, loading, error, enabled, setEnabled, layers, toggleLayer, applyLayers,
        reload: () => load(true),
        retry: () => load(false),
    };
}
